import { queryParamKeys } from 'modules/experiments/constants';
import {
  TableFilter,
  TableFilterQueryParamContextState,
  TableFilterQueryParamMap,
} from 'modules/experiments/types';
import { FC } from 'react';
import {
  serializationOptionsForNumberArrayValue,
  serializationOptionsForStringArrayValue,
  serializationOptionsForStringValue,
  useSearchParamState,
} from 'utils/hooks/useSearchParamState';
import { processTableFilterForSearchParam, updateMultiOptionSelection } from '../utils';
import {
  TableFilterQueryParamContext,
  initialTableFilterQueryParamContextState,
} from './TableFilterQueryParamContext';
import { useTablePaginationContext } from './TablePaginationContext';

const resetValue = '';

export const TableFilterQueryParamContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { resetPage } = useTablePaginationContext();

  /** Experiment Query Params */
  const [experimentModifiedBy, setExperimentModifiedBy] = useSearchParamState({
    searchParamKey: queryParamKeys.modifiedBy,
    stateValueWhenSearchParamIsUnset: initialTableFilterQueryParamContextState.experimentModifiedBy,
    ...serializationOptionsForStringArrayValue,
  });

  const [experimentTestSubject, setExperimentTestSubject] = useSearchParamState({
    searchParamKey: queryParamKeys.experimentTypes,
    stateValueWhenSearchParamIsUnset:
      initialTableFilterQueryParamContextState.experimentTestSubject,
    ...serializationOptionsForStringArrayValue,
  });

  const [experimentSystemConversionWinningCriteria, setExperimentSystemConversionWinningCriteria] =
    useSearchParamState({
      searchParamKey: queryParamKeys.winningCriteriaSystemConversion,
      stateValueWhenSearchParamIsUnset:
        initialTableFilterQueryParamContextState.experimentSystemConversionWinningCriteria,
      ...serializationOptionsForStringArrayValue,
    });

  const [experimentCustomConversionWinningCriteria, setExperimentCustomConversionWinningCriteria] =
    useSearchParamState({
      searchParamKey: queryParamKeys.winningCriteriaCustomConversion,
      stateValueWhenSearchParamIsUnset:
        initialTableFilterQueryParamContextState.experimentCustomConversionWinningCriteria,
      ...serializationOptionsForStringArrayValue,
    });

  const [experimentWinnerSelected, setExperimentWinnerSelected] = useSearchParamState({
    searchParamKey: queryParamKeys.winnerSelected,
    stateValueWhenSearchParamIsUnset:
      initialTableFilterQueryParamContextState.experimentWinnerSelected,
    ...serializationOptionsForStringValue,
  });

  const [experimentSplitVariationsRandomly, setExperimentSplitVariationsRandomly] =
    useSearchParamState({
      searchParamKey: queryParamKeys.splitVariationsEvenly,
      stateValueWhenSearchParamIsUnset:
        initialTableFilterQueryParamContextState.experimentSplitVariationsRandomly,
      ...serializationOptionsForStringValue,
    });

  /** Campaign Query Params */
  const [campaignLabels, setCampaignLabels] = useSearchParamState({
    searchParamKey: queryParamKeys.labelIds,
    stateValueWhenSearchParamIsUnset: initialTableFilterQueryParamContextState.campaignLabels,
    ...serializationOptionsForNumberArrayValue,
  });

  const [campaignMedium, setCampaignMedium] = useSearchParamState({
    searchParamKey: queryParamKeys.messageMediums,
    stateValueWhenSearchParamIsUnset: initialTableFilterQueryParamContextState.campaignMedium,
    ...serializationOptionsForStringArrayValue,
  });

  const [campaignTypes, setCampaignTypes] = useSearchParamState({
    searchParamKey: queryParamKeys.campaignTypes,
    stateValueWhenSearchParamIsUnset: initialTableFilterQueryParamContextState.campaignTypes,
    ...serializationOptionsForStringArrayValue,
  });

  /**
   * This map should let us to easily access the corresponding
   * search param controller for each table filter
   */
  const queryParamMap: TableFilterQueryParamMap = {
    [TableFilter.CAMPAIGN_LABELS]: {
      set: setCampaignLabels,
      value: campaignLabels,
      clear: () => setCampaignLabels(initialTableFilterQueryParamContextState.campaignLabels),
    },
    [TableFilter.CAMPAIGN_MEDIUM]: {
      set: setCampaignMedium,
      value: campaignMedium,
      clear: () => setCampaignMedium(initialTableFilterQueryParamContextState.campaignMedium),
    },
    [TableFilter.CAMPAIGN_TYPE]: {
      set: setCampaignTypes,
      value: campaignTypes,
      clear: () => setCampaignTypes(initialTableFilterQueryParamContextState.campaignTypes),
    },
    [TableFilter.EXPERIMENT_MODIFIED_BY]: {
      set: setExperimentModifiedBy,
      value: experimentModifiedBy,
      clear: () =>
        setExperimentModifiedBy(initialTableFilterQueryParamContextState.experimentModifiedBy),
    },
    [TableFilter.EXPERIMENT_TEST_SUBJECTS]: {
      set: setExperimentTestSubject,
      value: experimentTestSubject,
      clear: () =>
        setExperimentTestSubject(initialTableFilterQueryParamContextState.experimentTestSubject),
    },
    [TableFilter.EXPERIMENT_SYSTEM_WINNING_CRITERIA]: {
      set: setExperimentSystemConversionWinningCriteria,
      value: experimentSystemConversionWinningCriteria,
      clear: () =>
        setExperimentSystemConversionWinningCriteria(
          initialTableFilterQueryParamContextState.experimentSystemConversionWinningCriteria,
        ),
    },
    [TableFilter.EXPERIMENT_CUSTOM_WINNING_CRITERIA]: {
      set: setExperimentCustomConversionWinningCriteria,
      value: experimentCustomConversionWinningCriteria,
      clear: () =>
        setExperimentCustomConversionWinningCriteria(
          initialTableFilterQueryParamContextState.experimentCustomConversionWinningCriteria,
        ),
    },
    [TableFilter.EXPERIMENT_WINNER_SELECTED]: {
      set: setExperimentWinnerSelected,
      value: experimentWinnerSelected,
      clear: () =>
        setExperimentWinnerSelected(
          initialTableFilterQueryParamContextState.experimentWinnerSelected,
        ),
    },
    [TableFilter.EXPERIMENT_SPLIT_VARIATIONS_RANDOMLY]: {
      set: setExperimentSplitVariationsRandomly,
      value: experimentSplitVariationsRandomly,
      clear: () =>
        setExperimentSplitVariationsRandomly(
          initialTableFilterQueryParamContextState.experimentSplitVariationsRandomly,
        ),
    },
  };

  /**
   * Classifies the search param handler based on the type of data they handle.
   * And, does the appropriate data processing for each type.
   */
  const updateQueryParam = (tableFilter: TableFilter, newValue: string) => {
    resetPage();
    const processedFilterId = processTableFilterForSearchParam(tableFilter, newValue);

    switch (processedFilterId) {
      case TableFilter.CAMPAIGN_MEDIUM:
      case TableFilter.CAMPAIGN_TYPE:
      case TableFilter.EXPERIMENT_MODIFIED_BY:
      case TableFilter.EXPERIMENT_TEST_SUBJECTS:
      case TableFilter.EXPERIMENT_SYSTEM_WINNING_CRITERIA:
      case TableFilter.EXPERIMENT_CUSTOM_WINNING_CRITERIA: {
        const { set, value } = queryParamMap[processedFilterId];
        set(updateMultiOptionSelection(value, newValue));
        break;
      }

      case TableFilter.EXPERIMENT_WINNER_SELECTED:
      case TableFilter.EXPERIMENT_SPLIT_VARIATIONS_RANDOMLY: {
        const { set, value: oldValue } = queryParamMap[processedFilterId];
        set(newValue === oldValue ? resetValue : newValue);
        break;
      }

      case TableFilter.CAMPAIGN_LABELS: {
        const { set, value } = queryParamMap[processedFilterId];
        const labelId = parseInt(newValue, 10);
        set(updateMultiOptionSelection(value, labelId));
        break;
      }

      default:
        break;
    }
  };

  const clearQueryParam = () => Object.values(queryParamMap).forEach(({ clear }) => clear());

  const context: TableFilterQueryParamContextState = {
    /** Experiment Filters */
    experimentModifiedBy,
    setExperimentModifiedBy,
    experimentTestSubject,
    setExperimentTestSubject,
    experimentSystemConversionWinningCriteria,
    setExperimentSystemConversionWinningCriteria,
    experimentCustomConversionWinningCriteria,
    setExperimentCustomConversionWinningCriteria,
    experimentWinnerSelected,
    setExperimentWinnerSelected,
    experimentSplitVariationsRandomly,
    setExperimentSplitVariationsRandomly,

    /** Campaign Filters */
    campaignLabels,
    setCampaignLabels,
    campaignMedium,
    setCampaignMedium,
    campaignTypes,
    setCampaignTypes,

    /** Update Query Params */
    queryParamMap,
    updateQueryParam,
    clearQueryParam,
  };

  return (
    <TableFilterQueryParamContext.Provider value={context}>
      {children}
    </TableFilterQueryParamContext.Provider>
  );
};
